import { Component, EventEmitter, Input, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FileViewerUtils } from "../../utils/file-viewer.util";

export interface CoreFileCardRemoved {
  removedAt: string;
  removedByLabel?: string;
}

/**
 * Shared file-card rendering for chat attachments and the Documents hub grid (HAN-409 §2):
 * an image renders as a full-width thumbnail with a filename/size footer, everything else as a
 * horizontal card with a tinted icon block. Both states carry a view affordance and, for the
 * removed (tombstone) state, no thumbnail/actions at all (HAN-408 §6.3 / HAN-409 §5.4).
 */
@Component({
  selector: "core-file-card",
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (removed && layout === 'tile') {
      <div class="cfc cfc--tile cfc--tile-tombstone">
        <div class="cfc__tile-preview cfc__tile-preview--muted">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M9.5 12.5l5 5M14.5 12.5l-5 5" />
          </svg>
        </div>
        <div class="cfc__footer">
          <span class="cfc__name cfc__name--muted">{{ name }}</span>
          <span class="cfc__meta">Removed {{ removed.removedAt | date: "d MMM y" }}</span>
        </div>
      </div>
    } @else if (removed) {
      <div class="cfc cfc--tombstone">
        <span class="cfc__icon cfc__icon--muted">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M9.5 12.5l5 5M14.5 12.5l-5 5" />
          </svg>
        </span>
        <span class="cfc__body">
          <span class="cfc__name cfc__name--muted">{{ name }}</span>
          <span class="cfc__meta">Removed {{ removed.removedAt | date: "d MMM y" }}</span>
        </span>
      </div>
    } @else if (kind === "photo") {
      <div class="cfc cfc--image" tabindex="0" role="button" [attr.aria-label]="'View ' + name" (click)="view.emit()" (keydown.enter)="view.emit()" (keydown.space)="onSpace($event)">
        <img [src]="viewUrl" [alt]="name" class="cfc__thumb" />
        <div class="cfc__footer">
          <span class="cfc__name">{{ name }}</span>
          <span class="cfc__meta">{{ secondaryLine }}</span>
        </div>
        @if (removing) {
          <span class="cfc__remove cfc__remove--loading" aria-hidden="true">
            <svg class="cfc__spinner" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M12 3a9 9 0 1 0 9 9" stroke-linecap="round" />
            </svg>
          </span>
        } @else if (canRemove) {
          <button type="button" class="cfc__remove" [attr.aria-label]="'Remove ' + name" title="Remove this file" (click)="onRemove($event)">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        }
      </div>
    } @else if (layout === 'tile') {
      <div class="cfc cfc--tile" tabindex="0" role="button" [attr.aria-label]="'View ' + name" (click)="view.emit()" (keydown.enter)="view.emit()" (keydown.space)="onSpace($event)">
        <div class="cfc__tile-preview">
          <svg *ngIf="isPdf" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" /><path d="M9 15h1.5a1.5 1.5 0 0 0 0-3H9v5" /><path d="M13 12v5h1.5a2 2 0 0 0 0-4H13" /><path d="M18 12h-2v5" />
          </svg>
          <svg *ngIf="!isPdf" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
          </svg>
          <span class="cfc__tile-badge">{{ typeBadge }}</span>
        </div>
        <div class="cfc__footer">
          <span class="cfc__name">{{ name }}</span>
          <span class="cfc__meta">{{ secondaryLine }}</span>
        </div>
        @if (removing) {
          <span class="cfc__remove cfc__remove--loading" aria-hidden="true">
            <svg class="cfc__spinner" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M12 3a9 9 0 1 0 9 9" stroke-linecap="round" />
            </svg>
          </span>
        } @else if (canRemove) {
          <button type="button" class="cfc__remove" [attr.aria-label]="'Remove ' + name" title="Remove this file" (click)="onRemove($event)">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        }
      </div>
    } @else {
      <div class="cfc cfc--file" tabindex="0" role="button" [attr.aria-label]="'View ' + name" (click)="view.emit()" (keydown.enter)="view.emit()" (keydown.space)="onSpace($event)">
        <span class="cfc__icon">
          <svg *ngIf="isPdf" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" /><path d="M9 15h1.5a1.5 1.5 0 0 0 0-3H9v5" /><path d="M13 12v5h1.5a2 2 0 0 0 0-4H13" /><path d="M18 12h-2v5" />
          </svg>
          <svg *ngIf="!isPdf" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
          </svg>
        </span>
        <span class="cfc__body">
          <span class="cfc__name">{{ name }}</span>
          <span class="cfc__meta">{{ secondaryLine }}</span>
        </span>
        @if (removing) {
          <span class="cfc__remove cfc__remove--inline cfc__remove--loading" aria-hidden="true">
            <svg class="cfc__spinner" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M12 3a9 9 0 1 0 9 9" stroke-linecap="round" />
            </svg>
          </span>
        } @else if (canRemove) {
          <button type="button" class="cfc__remove cfc__remove--inline" [attr.aria-label]="'Remove ' + name" title="Remove this file" (click)="onRemove($event)">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        } @else {
          <span class="cfc__view" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
            </svg>
          </span>
        }
      </div>
    }
  `,
  styles: [
    `
      .cfc {
        position: relative;
        border: 1px solid #e4e4e7;
        border-radius: 12px;
        overflow: hidden;
        cursor: pointer;
        background: #fff;
        transition:
          border-color 0.15s ease,
          box-shadow 0.15s ease;
      }
      .cfc--tombstone {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        background: rgba(102, 112, 133, 0.04);
        cursor: default;
      }
      .cfc--tile-tombstone {
        cursor: default;
        background: rgba(102, 112, 133, 0.04);
      }
      .cfc--file {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 10px;
      }
      .cfc--file:hover,
      .cfc--image:hover,
      .cfc--tile:hover {
        border-color: #4e36f5;
        box-shadow: 0 1px 6px rgba(78, 54, 245, 0.12);
      }
      .cfc--file:focus-visible,
      .cfc--image:focus-visible,
      .cfc--tile:focus-visible {
        outline: 2px solid #4e36f5;
        outline-offset: 1px;
      }
      .cfc--tile-tombstone:hover {
        border-color: #e4e4e7;
        box-shadow: none;
      }
      .cfc__icon {
        flex-shrink: 0;
        width: 36px;
        height: 36px;
        border-radius: 8px;
        background: rgba(78, 54, 245, 0.1);
        color: #4e36f5;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .cfc__icon--muted {
        background: rgba(102, 112, 133, 0.08);
        color: #98a2b3;
      }
      .cfc__body {
        min-width: 0;
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      .cfc__name {
        font-size: 13px;
        font-weight: 500;
        color: #212636;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .cfc__name--muted {
        color: #98a2b3;
        font-weight: 400;
      }
      .cfc__meta {
        font-size: 11px;
        color: #98a2b3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .cfc__view {
        flex-shrink: 0;
        color: #98a2b3;
        display: flex;
        align-items: center;
      }
      .cfc__thumb {
        display: block;
        width: 100%;
        height: 100px;
        object-fit: cover;
        background: #f4f2fe;
      }
      .cfc__tile-preview {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 84px;
        background: rgba(78, 54, 245, 0.08);
        color: #4e36f5;
      }
      .cfc__tile-preview--muted {
        background: rgba(102, 112, 133, 0.06);
        color: #b8b5c6;
      }
      .cfc__tile-badge {
        position: absolute;
        top: 6px;
        right: 6px;
        font-size: 9px;
        font-weight: 600;
        letter-spacing: 0.02em;
        background: #fff;
        color: #667085;
        padding: 1px 5px;
        border-radius: 7px;
      }
      .cfc__footer {
        display: flex;
        flex-direction: column;
        gap: 1px;
        padding: 8px 10px;
      }
      .cfc__remove {
        border: none;
        background: rgba(15, 17, 22, 0.6);
        color: #fff;
        border-radius: 999px;
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 6px;
        right: 6px;
        cursor: pointer;
      }
      .cfc__remove:hover {
        background: rgba(15, 17, 22, 0.8);
      }
      .cfc__remove:focus-visible {
        outline: 2px solid #fff;
        outline-offset: 1px;
      }
      .cfc__remove--inline {
        position: static;
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        background: transparent;
        color: #98a2b3;
      }
      .cfc__remove--inline:hover {
        background: rgba(240, 68, 56, 0.08);
        color: #f04438;
      }
      .cfc__remove--inline:focus-visible {
        outline: 2px solid #f04438;
        outline-offset: 1px;
      }
      .cfc__remove--loading {
        cursor: default;
        color: #98a2b3;
      }
      .cfc__remove--loading:hover {
        background: transparent;
      }
      .cfc__spinner {
        animation: cfc-spin 0.7s linear infinite;
      }
      @keyframes cfc-spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class CoreFileCardComponent {
  @Input({ required: true }) name!: string;
  @Input() fileType: string | null | undefined;
  @Input({ required: true }) kind!: "photo" | "document";
  @Input({ required: true }) viewUrl!: string;
  /** Caller-composed second line: e.g. a formatted size ("240 KB") in the thread, or a sender/date label in a grid. */
  @Input() secondaryLine = "";
  @Input() removed: CoreFileCardRemoved | null | undefined;
  @Input() canRemove = false;
  /** Shows a spinner in place of the remove control while a removal request is in flight. */
  @Input() removing = false;
  /** "row" (default) is the horizontal icon+name card used in the thread; "tile" renders a
   * document as a square preview with a corner type badge, matching an image card, for grids
   * (e.g. the files-in-conversation view) where every entry should read as one consistent set. */
  @Input() layout: "row" | "tile" = "row";

  @Output() view = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();

  get isPdf(): boolean {
    return FileViewerUtils.getFileKind(this.name, this.fileType) === "pdf";
  }

  get typeBadge(): string {
    if (this.isPdf) return "PDF";
    const ext = (this.name.split(".").pop() || "").toUpperCase();
    return ext.length <= 4 ? ext : "FILE";
  }

  onRemove(event: Event): void {
    event.stopPropagation();
    if (this.removing) return;
    this.remove.emit();
  }

  onSpace(event: Event): void {
    event.preventDefault();
    this.view.emit();
  }
}
